import React, { useState } from 'react';

import MainButton from '../../../components/agv-button';
import InputText from '../../../components/agv-forms-input';
import isEmailValid from '../../../utils/is-email-valid.util';
import isPhoneNumberValid from '../../../utils/is-phone-number-valid.util';
import { Banner, Card } from '../clients-ui';

const EMPTY_USER = {
  first_name: '',
  last_name: '',
  email: '',
  phone_number: '',
};

export default function AdminUserStep({
  adminUsers,
  onCreate,
  isLoading,
  isEmailSendingEnabled,
}) {
  const [user, setUser] = useState(EMPTY_USER);

  const update = (key) => (value) =>
    setUser((previous) => ({ ...previous, [key]: value }));

  const isPhoneInvalid =
    user.phone_number.length > 0 && !isPhoneNumberValid(user.phone_number);

  const isDisabled =
    !user.first_name.trim() ||
    !user.last_name.trim() ||
    !isEmailValid(user.email) ||
    isPhoneInvalid;

  const handleCreate = async () => {
    const isCreated = await onCreate({
      ...user,
      phone_number: user.phone_number || undefined,
    });

    if (isCreated) setUser(EMPTY_USER);
  };

  return (
    <div className="tw-flex tw-flex-col tw-gap-4">
      <Card
        title="Administrateur du client"
        subtitle="Rôle ADMIN : il gère ensuite les utilisateurs, véhicules et paramètres de son client, et n’a pas accès aux outils internes (réservés aux SUPER-ADMIN Akanéa). Un email d’invitation lui permet de choisir son mot de passe."
      >
        {!isEmailSendingEnabled && (
          <Banner tone="warn">
            L’envoi d’emails est désactivé pour ce client : l’administrateur
            sera créé, mais il ne recevra pas d’email d’invitation et ne pourra
            donc pas choisir son mot de passe. Activez « Envoi d’emails » à
            l’étape Informations (puis enregistrez) avant de le créer.
          </Banner>
        )}
        <div className="tw-grid tw-grid-cols-2 tw-gap-4 mq750:tw-grid-cols-1">
          <InputText
            value={user.last_name}
            setValue={update('last_name')}
            fieldTitle="Nom"
            isRequired
            emptyError={'Le champ "Nom" ne peut être vide.'}
            placeholder="Leblanc"
          />
          <InputText
            value={user.first_name}
            setValue={update('first_name')}
            fieldTitle="Prénom"
            isRequired
            emptyError={'Le champ "Prénom" ne peut être vide.'}
            placeholder="Juste"
          />
          <InputText
            value={user.email}
            setValue={update('email')}
            fieldTitle="E-mail"
            isRequired
            emptyError={'Le champ "E-mail" ne peut être vide.'}
            placeholder="exemple@exemple.com"
            isBadFormat={!isEmailValid(user.email)}
            type="email"
          />
          <InputText
            value={user.phone_number}
            setValue={update('phone_number')}
            fieldTitle="Téléphone"
            placeholder="0612345678"
            isBadFormat={isPhoneInvalid}
            type="tel"
            maxLength={10}
          />
        </div>
        <div className="tw-flex tw-justify-end">
          <MainButton
            text={
              isEmailSendingEnabled
                ? 'Créer et envoyer l’invitation'
                : 'Créer sans invitation'
            }
            onClick={handleCreate}
            disabled={isDisabled}
            isLoading={isLoading}
          />
        </div>
      </Card>

      <Card
        title="Administrateurs du client"
        subtitle={
          adminUsers.length === 0
            ? 'Aucun administrateur : le client ne pourra pas se connecter au backoffice.'
            : `${adminUsers.length} administrateur${adminUsers.length > 1 ? 's' : ''}`
        }
      >
        {adminUsers.length > 0 && (
          <div className="tw-overflow-hidden tw-rounded-[6px] tw-border tw-border-solid tw-border-[#e6e8ec]">
            {adminUsers.map((adminUser) => (
              <div
                key={adminUser.id}
                className="tw-flex tw-items-center tw-gap-3 tw-border-0 tw-border-b tw-border-solid tw-border-[#eeeff2] tw-px-3 tw-py-2 last:tw-border-b-0"
              >
                <span className="tw-flex tw-size-7 tw-items-center tw-justify-center tw-rounded-full tw-bg-main tw-text-[11px] tw-font-semibold tw-text-white">
                  {`${adminUser.first_name?.[0] || ''}${adminUser.last_name?.[0] || ''}`.toUpperCase()}
                </span>
                <span className="tw-flex-1 tw-text-[13px] tw-font-medium tw-text-black">
                  {adminUser.first_name} {adminUser.last_name}
                </span>
                <span className="tw-text-[12px] tw-text-[#76808c]">
                  {adminUser.email}
                </span>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
